import { cn } from '@/lib/utils';
import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next'
import { ArticleImage, ImageImage, MomentsImage, PodcastImage, VideoImage } from './assets';

interface SettingsTabsProps {
  activeTab: string;
  subscriptionVisibility: number[];
  changeActiveTab: (tab: string) => void;
}

/** 设置标签页 */
export const SettingsTabs = ({
  activeTab,
  subscriptionVisibility,
  changeActiveTab
}: SettingsTabsProps) => {
  const { t } = useTranslation()
  const indicatorRef = useRef<HTMLDivElement>(null)
  const tabRefs = useRef<{ [key: string]: HTMLButtonElement | null }>({})

  // Tab选项定义
  const tabs = useMemo(() => {
    return [
      {
        key: 'video',
        label: t('agent_dashboard.user_detail.setting_tabs.video_setting'),
        icon: <VideoImage className='w-5 h-5 mr-2' />,
        visible: subscriptionVisibility.includes(1)
      },
      {
        key: 'podcast',
        label: t('agent_dashboard.user_detail.setting_tabs.podcast_setting'),
        icon: <PodcastImage className='w-5 h-5 mr-2' />,
        visible: subscriptionVisibility.includes(2)
      },
      {
        key: 'moments',
        label: t('agent_dashboard.user_detail.setting_tabs.moments_setting'),
        icon: <MomentsImage className='w-5 h-5 mr-2' />,
        visible: subscriptionVisibility.includes(3)
      },
      {
        key: 'image',
        label: t('agent_dashboard.user_detail.setting_tabs.image_setting'),
        icon: <ImageImage className='w-5 h-5 mr-2' />,
        visible: subscriptionVisibility.includes(4)
      },
      {
        key: 'article',
        label: t('agent_dashboard.user_detail.setting_tabs.article_setting'),
        icon: <ArticleImage className='w-5 h-5 mr-2' />,
        visible: subscriptionVisibility.includes(5)
      }
    ]
  }, [subscriptionVisibility]);

  // 监听tab变化，更新底部背景位置
  useEffect(() => {
    const activeTabElement = tabRefs.current[activeTab];
    const indicatorElement = indicatorRef.current;

    if (activeTabElement && indicatorElement) {
      const { left, width } = activeTabElement.getBoundingClientRect();
      const containerLeft = activeTabElement.parentElement?.getBoundingClientRect().left || 0;
      const offsetLeft = left - containerLeft;

      indicatorElement.style.transform = `translateX(${offsetLeft}px)`;
      indicatorElement.style.width = `${width}px`;
    }
  }, [activeTab, subscriptionVisibility]);

  return (
    <div className="relative border-b border-[rgba(224,227,230,1)]">
      <div className="flex overflow-x-auto scrollbar-hide">
        {tabs.map((tab) => tab.visible && (
          <button
            key={tab.key}
            ref={(el) => (tabRefs.current[tab.key] = el)}
            className={cn(
              'flex items-center px-2 pl-1 py-3 text-sm mr-6  transition-all whitespace-nowrap',
              activeTab === tab.key ? 'text-[rgba(16,24,40,1)]' : 'text-[rgba(102,112,133,1)]',
            )}
            onClick={() => changeActiveTab(tab.key)}
          >
            {tab.icon}
            <span className='text-[17px] font-medium'>{tab.label}</span>
          </button>
        ))}
      </div>
      {/* 底部激活横条 */}
      <div
        ref={indicatorRef}
        className="absolute bottom-0 left-0 h-[2px] bg-[#101828] transition-all duration-300 ease-out"
        style={{ width: '0px', transform: 'translateX(0px)' }}
      />
    </div>
  )
}
